import React, { useCallback, useEffect, useMemo } from 'react';
import { SafeAreaView, TouchableOpacity, View } from 'react-native';
import { SimpleSteps, StepsForm } from '~/components';
import {
  DocumentStepPage,
  EmailStepPage,
  FinishStepPage,
  PasswordStepPage,
  PersonalInfoStepPage,
} from './components';
import useAppNavigation from '~/hooks/useAppNavigation';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';
import { useCreateAccountForm } from '~/stores';

import MarisaServiceLogoSVG from '~/assets/svgs/marisaServicesLogo.svg';
import MarisaSVG from '~/assets/svgs/marisa.svg';
import CloseSVG from '~/assets/svgs/close.svg';
import type { StepComponentProps } from '~/components/StepsForm';

export interface CreateAccuntStepsProps extends StepComponentProps {
  onContinue: () => void;
  goToStep?: (step?: number) => void;
}

const CreateAccount: React.FC = () => {
  const { push, navigate } = useAppNavigation();
  const { form } = useCreateAccountForm();

  const { params } =
    useRoute<RouteProp<RootStackParamList, 'MarisaServicos_CreateAccount'>>();

  const currentStep = useMemo(() => {
    return Number(params?.step || 1);
  }, [params?.step]);

  const goToStep = useCallback(
    (step: number) => {
      push('MarisaServicos_CreateAccount', {
        step,
      });
    },
    [push]
  );

  const onContinue = useCallback(() => {
    goToStep(currentStep + 1);
  }, [currentStep, goToStep]);

  const stepsFormData = useMemo(
    () => [
      {
        step: 1,
        content: <EmailStepPage onContinue={onContinue} />,
      },
      {
        step: 2,
        content: <DocumentStepPage onContinue={onContinue} />,
      },
      {
        step: 3,
        content: <PersonalInfoStepPage onContinue={onContinue} />,
      },
      {
        step: 4,
        content: <PasswordStepPage onContinue={onContinue} />,
      },
      {
        step: 5,
        content: <FinishStepPage />,
      },
    ],
    [onContinue]
  );

  useEffect(() => {
    if (form) {
      const { email, document, personalInfo, password } = form;

      const steps: any[] = [email, document, personalInfo, password];

      if (
        currentStep >= 2 &&
        Object.entries(steps[currentStep - 2]).length === 0
      ) {
        navigate('MarisaServicos_CreateAccount', {});
      }
    }
  }, [currentStep, form, navigate]);

  return (
    <SafeAreaView className="bg-white h-full">
      <View className="bg-white flex-row h-full mb-24">
        <View className="flex-1 bg-primary-600 items-center justify-center hidden lg:flex">
          <MarisaServiceLogoSVG />
        </View>

        <View className="flex-1 bg-white items-center justify-start px-10 pt-24 lg:pt-[8%]">
          {currentStep !== stepsFormData.length && (
            <>
              <MarisaSVG className="mb-6" />

              <SimpleSteps length={stepsFormData.length} step={currentStep} />
            </>
          )}

          <StepsForm data={stepsFormData} currentStep={currentStep} />
        </View>

        <TouchableOpacity
          className="absolute top-5 right-5 z-10"
          onPress={() => navigate('MarisaServicos_SignIn')}
        >
          <CloseSVG height={24} width={24} />
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

export default CreateAccount;
